/**
 * Plans API Service
 * Handles plan and entitlement operations for client/tenant.
 * Client entitlements (GET /clients/{clientId}/entitlements) include plan entitlements plus any additional client-specific entitlements.
 *
 * Base URL: /api (apiFetch adds /api prefix)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "@/hooks/use-api";
import type { PlanEntitlementListResponseDto, PlanEntitlementResponseDto } from "@/types/api/plans";

const CLIENTS_BASE = "/clients";

/** Entitlement code for External Export feature (align with backend) */
export const EXTERNAL_EXPORT_ENTITLEMENT_CODE = "EXTERNAL_EXPORT";

/** Entitlement code for Routing Codes feature (align with backend) */
export const FEATURE_ROUTING_CODES_ENTITLEMENT_CODE = "FEATURE_ROUTING_CODES";

/** Entitlement code for GL code suggestions (align with backend; per-client add-on only) */
export const FEATURE_GL_SUGGEST_ENTITLEMENT_CODE = "FEATURE_GL_SUGGEST";

/**
 * Hook providing client entitlement API operations with authentication
 */
export function usePlansApi() {
    const api = useApi();

    /**
     * Get all entitlements for a client (plan + any additional client-specific entitlements)
     * GET /clients/{clientId}/entitlements
     */
    const getClientEntitlements = useCallback(
        async (clientId: number): Promise<PlanEntitlementResponseDto[]> => {
            try {
                const response = await api.get<PlanEntitlementListResponseDto>(`${CLIENTS_BASE}/${clientId}/entitlements`);
                return response.data ?? [];
            } catch {
                return [];
            }
        },
        [api],
    );

    return useMemo(
        () => ({
            getClientEntitlements,
        }),
        [getClientEntitlements],
    );
}

export interface UsePlanEntitlementsResult {
    /** Whether the client has the External Export entitlement enabled */
    hasExternalExportEntitlement: boolean;
    /** Whether the client has the Routing Codes entitlement enabled */
    hasRoutingCodesEntitlement: boolean;
    /** Whether the client has the GL code suggestions entitlement enabled */
    hasGlSuggestEntitlement: boolean;
    /** Whether entitlement data is still loading */
    isLoading: boolean;
}

/**
 * Hook to resolve client entitlements for a given client id and expose
 * whether the External Export entitlement is enabled.
 * Uses GET /clients/{clientId}/entitlements (plan + any additional client entitlements).
 * Pass null when no client id is available (e.g. no documents loaded).
 */
export function usePlanEntitlements(clientId: number | null): UsePlanEntitlementsResult {
    const { getClientEntitlements } = usePlansApi();
    const [hasExternalExportEntitlement, setHasExternalExportEntitlement] = useState(false);
    const [hasRoutingCodesEntitlement, setHasRoutingCodesEntitlement] = useState(false);
    const [hasGlSuggestEntitlement, setHasGlSuggestEntitlement] = useState(false);
    const [isLoading, setIsLoading] = useState(!!clientId);

    useEffect(() => {
        if (!clientId) {
            setHasExternalExportEntitlement(false);
            setHasRoutingCodesEntitlement(false);
            setHasGlSuggestEntitlement(false);
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        const load = async () => {
            try {
                const entitlements = await getClientEntitlements(clientId);
                if (cancelled) return;
                const hasExternal = entitlements.some((e) => e.code === EXTERNAL_EXPORT_ENTITLEMENT_CODE && e.isEnabled);
                const hasRouting = entitlements.some((e) => e.code === FEATURE_ROUTING_CODES_ENTITLEMENT_CODE && e.isEnabled);
                const hasGlSuggest = entitlements.some((e) => e.code === FEATURE_GL_SUGGEST_ENTITLEMENT_CODE && e.isEnabled);
                setHasExternalExportEntitlement(hasExternal);
                setHasRoutingCodesEntitlement(hasRouting);
                setHasGlSuggestEntitlement(hasGlSuggest);
            } catch {
                if (!cancelled) {
                    setHasExternalExportEntitlement(false);
                    setHasRoutingCodesEntitlement(false);
                    setHasGlSuggestEntitlement(false);
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [clientId, getClientEntitlements]);

    return useMemo(
        () => ({ hasExternalExportEntitlement, hasRoutingCodesEntitlement, hasGlSuggestEntitlement, isLoading }),
        [hasExternalExportEntitlement, hasRoutingCodesEntitlement, hasGlSuggestEntitlement, isLoading],
    );
}
